import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Tag } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Form, Input, Modal, Select, Table } from '@/components/index';
import {
  addAdminUserRequest,
  getAdminRoleListRequest,
  getAdminUserListRequest,
  updateAdminUserRequest,
  updateAdminUserStatusRequest,
} from '@/state/request/adminUser';
import './style.less';

const STATUS_ALL = 'all';
const PHONE_REG = /^1[3-9]\d{9}$/;

const trimValue = (value) => (typeof value === 'string' ? value.trim() : value);

const SystemUserManagement = () => {
  const [searchForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [roleList, setRoleList] = useState([]);

  const getUserList = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await getAdminUserListRequest({
        keyword: params.keyword ?? null,
        enable: params.enable ?? null,
      });
      if (res?.code === 200) {
        setDataSource(Array.isArray(res.data) ? res.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getRoleList = useCallback(async () => {
    const res = await getAdminRoleListRequest();
    if (res?.code === 200) {
      setRoleList(Array.isArray(res.data) ? res.data : []);
    }
  }, []);

  useEffect(() => {
    searchForm.setFieldsValue({ enable: STATUS_ALL });
    getUserList({ keyword: null, enable: null });
    getRoleList();
  }, [getRoleList, getUserList, searchForm]);

  const buildSearchParams = () => {
    const values = searchForm.getFieldsValue();
    return {
      keyword: trimValue(values.keyword) || null,
      enable: values.enable === STATUS_ALL || values.enable === undefined ? null : values.enable,
    };
  };

  const handleSearch = () => {
    getUserList(buildSearchParams());
  };

  const handleReset = () => {
    searchForm.setFieldsValue({ keyword: undefined, enable: STATUS_ALL });
    getUserList({ keyword: null, enable: null });
  };

  const handleAdd = () => {
    setEditingRecord(null);
    userForm.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    userForm.setFieldsValue({
      name: record.name,
      phone: record.phone,
      roleIdList: Array.isArray(record.roleList)
        ? record.roleList.map((item) => item.roleId)
        : [],
    });
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
    userForm.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await userForm.validateFields();
      setSaving(true);
      const name = trimValue(values.name);
      const roleIdList = values.roleIdList || [];
      const payload = editingRecord
        ? {
            userId: editingRecord.userId,
            name,
            roleIdList,
          }
        : {
            name,
            phone: trimValue(values.phone),
            roleIdList,
          };
      const api = editingRecord ? updateAdminUserRequest : addAdminUserRequest;
      const res = await api(payload);
      if (res?.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        handleModalCancel();
        getUserList(buildSearchParams());
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (record) => {
    setUpdatingId(record.userId);
    try {
      const res = await updateAdminUserStatusRequest({
        userId: record.userId,
        enable: !record.enable,
      });
      if (res?.code === 200) {
        message.success(record.enable ? '禁用成功' : '启用成功');
        getUserList(buildSearchParams());
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const roleOptions = useMemo(
    () =>
      roleList.map((item) => ({
        label: item.remark || item.roleName || item.key,
        value: item.roleId,
      })),
    [roleList],
  );

  const columns = useMemo(
    () => [
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
        width: 260,
      },
      {
        title: '手机号',
        dataIndex: 'phone',
        key: 'phone',
        width: 160,
      },
      {
        title: '角色',
        dataIndex: 'roleList',
        key: 'roleList',
        render: (value) => {
          const roleText =
            Array.isArray(value) && value.length
              ? value.map((item) => item.roleName || item.remark).filter(Boolean).join(' | ')
              : '--';
          return <div className='system-user-role-cell'>{roleText}</div>;
        },
      },
      {
        title: '状态',
        dataIndex: 'enable',
        key: 'enable',
        width: 100,
        render: (enable) =>
          enable ? <Tag color='success'>启用</Tag> : <Tag className='system-user-disabled-tag'>禁用</Tag>,
      },
      {
        title: '操作',
        key: 'action',
        width: 160,
        render: (_, record) => (
          <div className='system-user-actions'>
            <button type='button' onClick={() => handleEdit(record)}>
              编辑
            </button>
            <button
              type='button'
              className={record.enable ? 'system-user-action-danger' : 'system-user-action-success'}
              disabled={updatingId === record.userId}
              onClick={() => handleToggle(record)}
            >
              {record.enable ? '禁用' : '启用'}
            </button>
          </div>
        ),
      },
    ],
    [updatingId],
  );

  return (
    <PageHeaderLayout>
      <div className='system-user-management'>
        <Card bordered={false}>
          <Form form={searchForm} className='system-user-search' layout='inline'>
            <Form.Item name='keyword'>
              <Input
                style={{ width: 200 }}
                placeholder='请输入姓名或手机号'
                onPressEnter={handleSearch}
              />
            </Form.Item>
            <Form.Item name='enable'>
              <Select
                style={{ width: 120 }}
                allowClear={false}
                options={[
                  { label: '全部状态', value: STATUS_ALL },
                  { label: '启用', value: true },
                  { label: '禁用', value: false },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button onClick={handleSearch}>查询</Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={handleReset}>重置</Button>
            </Form.Item>
            <Form.Item>
              <Button type='primary' onClick={handleAdd}>
                新增
              </Button>
            </Form.Item>
          </Form>
          <Table
            rowKey='userId'
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            tableLayout='fixed'
          />
        </Card>
        <Modal
          title={editingRecord ? '编辑用户' : '新增用户'}
          open={modalVisible}
          onCancel={handleModalCancel}
          footer={
            <div className='system-user-modal-footer'>
              <Button onClick={handleModalCancel}>取消</Button>
              <Button type='primary' loading={saving} onClick={handleModalOk}>
                保存
              </Button>
            </div>
          }
          destroyOnClose
          maskClosable={false}
          width={440}
        >
          <Form
            form={userForm}
            className='system-user-form'
            labelCol={{ flex: '72px' }}
            wrapperCol={{ flex: '1' }}
          >
            <Form.Item
              label='姓名'
              name='name'
              normalize={trimValue}
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input placeholder='请输入姓名' maxLength={20} />
            </Form.Item>
            <Form.Item
              label='手机号'
              name='phone'
              normalize={trimValue}
              rules={
                editingRecord
                  ? []
                  : [
                      { required: true, message: '请输入手机号' },
                      { pattern: PHONE_REG, message: '请输入正确的手机号' },
                    ]
              }
            >
              <Input placeholder='请输入手机号' maxLength={11} disabled={!!editingRecord} />
            </Form.Item>
            <Form.Item
              label='角色'
              name='roleIdList'
              rules={[{ required: true, type: 'array', min: 1, message: '请选择角色' }]}
            >
              <Select
                multiple
                placeholder='请选择角色'
                options={roleOptions}
                showSearch
                maxTagCount={3}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </PageHeaderLayout>
  );
};

export default SystemUserManagement;
